import React, { useEffect, useMemo, useState } from "react";
import { ActivityCard, MixedText, PlayButton, ProgressPill } from "../shared";

/**
 * VerbMachine — "The Prefix Machine" conjugator for the present-tense lesson.
 *
 * Verb tabs on top, the five singular pronouns below. Tapping a pronoun
 * snaps its prefix (and, for أَنْتِ, the ينَ suffix) onto the verb — both
 * highlighted — and plays the form. Completes when every pronoun has been
 * tried on every verb.
 */
function HighlightedForm({ form }) {
  const prefix = form.prefix && form.ar.startsWith(form.prefix) ? form.prefix : "";
  const suffix = form.suffix && form.ar.endsWith(form.suffix) ? form.suffix : "";
  const stem = form.ar.slice(prefix.length, form.ar.length - suffix.length);
  return (
    <>
      <span style={{ color: "var(--gold-bright)" }}>{prefix}</span>
      {stem}
      {suffix && <span style={{ color: "var(--tag-violet)" }}>{suffix}</span>}
    </>
  );
}

export default function VerbMachine({ data, onComplete, audio }) {
  const [verbIdx, setVerbIdx] = useState(0);
  const [pronounId, setPronounId] = useState(null);
  const [visited, setVisited] = useState(() => new Set());

  const verb = data.verbs[verbIdx];
  const total = data.verbs.length * data.pronouns.length;
  const done = visited.size >= total;
  const form = useMemo(
    () => (pronounId ? verb.forms.find((f) => f.pronoun === pronounId) : null),
    [verb, pronounId]
  );

  useEffect(() => {
    if (done) onComplete();
  }, [done, onComplete]);

  const handlePronoun = (p) => {
    setPronounId(p.id);
    const picked = verb.forms.find((f) => f.pronoun === p.id);
    setVisited((prev) => {
      const next = new Set(prev);
      next.add(`${verb.id}:${p.id}`);
      return next;
    });
    if (audio && picked) audio.play(picked.ar);
  };

  const handleVerb = (idx) => {
    setVerbIdx(idx);
    setPronounId(null);
  };

  return (
    <ActivityCard>
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          {data.verbs.map((v, idx) => {
            const verbDone = data.pronouns.every((p) => visited.has(`${v.id}:${p.id}`));
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => handleVerb(idx)}
                className="rounded-lg px-3 py-1.5 text-sm font-semibold transition-all hover:brightness-110"
                style={{
                  background: idx === verbIdx ? "var(--gold-bg)" : "var(--ink)",
                  border: idx === verbIdx ? "1px solid var(--gold-dim)" : "1px solid var(--panel-line)",
                  color: verbDone ? "var(--gold)" : "var(--parchment)",
                }}
              >
                {v.en}
                {verbDone && <span aria-hidden="true"> ✓</span>}
              </button>
            );
          })}
        </div>
        <ProgressPill>{visited.size}/{total} forms tried</ProgressPill>
      </div>

      <div className="text-center min-h-[9rem]">
        {form ? (
          <>
            <p dir="rtl" lang="ar" className="font-arabic text-6xl leading-[1.6] m-0" style={{ color: "var(--parchment)" }}>
              <HighlightedForm form={form} />
            </p>
            <p className="text-base mt-2 mb-0 inline-flex items-center gap-3" style={{ color: "var(--parchment)" }}>
              {form.en}
              {audio && <PlayButton text={form.ar} playingText={audio.playingText} onPlay={audio.play} />}
            </p>
            {form.note && (
              <p className="text-sm mt-2 mb-0 px-4 py-2 rounded-lg inline-block" style={{ background: "var(--gold-bg)", color: "var(--parchment)" }}>
                <MixedText text={form.note} />
              </p>
            )}
          </>
        ) : (
          <p className="text-base mt-8 mb-0 animate-pulse" style={{ color: "var(--parchment-dim)" }}>
            Pick a person below to run the machine.
          </p>
        )}
      </div>

      <div dir="rtl" className="flex flex-wrap justify-center gap-2 mt-5">
        {data.pronouns.map((p) => {
          const seen = visited.has(`${verb.id}:${p.id}`);
          const active = pronounId === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => handlePronoun(p)}
              className="rounded-xl px-3.5 py-2 text-center transition-all hover:brightness-110"
              style={{
                background: active ? "var(--gold-bg)" : "var(--ink)",
                border: active
                  ? "1px solid var(--gold-dim)"
                  : seen
                  ? "1px solid var(--tag-violet)"
                  : "1px solid var(--panel-line)",
              }}
            >
              <span lang="ar" className="font-arabic text-2xl leading-[1.6] block" style={{ color: "var(--parchment)" }}>
                {p.ar}
              </span>
              <span className="text-[11px] block" style={{ color: "var(--parchment-dim)" }}>{p.en}</span>
            </button>
          );
        })}
      </div>

      {done && (
        <div
          className="flex items-start gap-2.5 mt-4 px-4 py-3 rounded-xl"
          style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">⚙️</span>
          <p className="text-sm leading-relaxed m-0" style={{ color: "var(--parchment)" }}>
            <MixedText text={data.takeaway} />
          </p>
        </div>
      )}
    </ActivityCard>
  );
}
